import { Link } from "react-router-dom";
import { ClipboardCheck, ShieldCheck, DollarSign, Shirt, CalendarClock, Plane, Megaphone, Phone, Mail, Lock } from "lucide-react";
import { useFetch, fmtDate, usePageTitle } from "@/lib/api";
import { IMG, SITE, TRYOUT } from "@/lib/content";
import { PageHero, SectionHeading, Reveal } from "@/components/site/Bits";
import { RegisterButtons } from "@/components/site/RegisterButtons";
import { FaqList } from "@/pages/Tryouts";

const Card = ({ icon: Icon, title, children, testid }) => (
  <Reveal className="card-navy p-6 sm:p-8">
    <div data-testid={testid}>
      <Icon className="h-7 w-7 text-gold" />
      <h3 className="mt-4 font-display text-2xl font-black uppercase italic">{title}</h3>
      <div className="mt-3 text-sm text-slate-300 space-y-2 leading-relaxed">{children}</div>
    </div>
  </Reveal>
);

const Packages = () => (
  <div className="grid gap-4 sm:grid-cols-2" data-testid="fee-packages">
    <div className="border border-line bg-pitch p-6">
      <p className="font-display text-6xl font-black italic text-gold">$80</p>
      <p className="font-display text-xl font-extrabold uppercase">Registration + Jersey</p>
      <ul className="mt-4 space-y-1 text-sm text-slate-300"><li>▸ Registration</li><li>▸ Jersey</li></ul>
    </div>
    <div className="border-2 border-gold bg-navy p-6 relative">
      <span className="absolute right-4 top-4 bg-gold px-2 py-0.5 font-mono text-[10px] uppercase text-navy">Full Package</span>
      <p className="font-display text-6xl font-black italic text-gold">$165</p>
      <p className="font-display text-xl font-extrabold uppercase">Complete Allstar Package</p>
      <ul className="mt-4 space-y-1 text-sm text-slate-300"><li>▸ Registration</li><li>▸ Uniform</li><li>▸ Sweatsuit</li><li>▸ Travel bag</li><li>▸ Compression shirt</li></ul>
    </div>
  </div>
);

export default function Parents() {
  usePageTitle("Parent Information Center");
  const { data: events } = useFetch("/content/events");
  const { data: ann } = useFetch("/content/announcements");
  return (
    <div data-testid="parents-page">
      <PageHero testid="parents-hero" eyebrow="Parent Information Center" image={IMG.huddle}
        title={<>Everything Your<br /><span className="text-gold">Family Needs.</span></>}
        sub="Registration, tryouts, fees, practices, tournaments and travel — all in one place.">
        <Link to="/portal" className="btn-gold mt-8" data-testid="parents-portal-button"><Lock className="h-4 w-4" /> Parent Portal Login</Link>
      </PageHero>

      <section className="bg-pitch py-16 sm:py-24">
        <div className="wrap grid gap-5 md:grid-cols-2">
          <Card icon={ClipboardCheck} title="Registration Instructions" testid="parents-registration">
            <ol className="space-y-2 list-decimal list-inside">
              <li>Complete the official player registration (Jotform).</li>
              <li>Secure your athlete's tryout spot (Eventbrite).</li>
              <li>Arrive for check-in at {TRYOUT.checkin} on {TRYOUT.date} at {TRYOUT.location}.</li>
              <li>Participate in the full selection process — invitations do not guarantee a roster spot.</li>
            </ol>
            <div className="pt-3"><RegisterButtons /></div>
          </Card>
          <Card icon={ShieldCheck} title="Tryout Requirements" testid="parents-requirements">
            <ul className="space-y-1.5">
              <li>▸ Ages {TRYOUT.ages}, subject to division eligibility</li>
              <li>▸ Previous football experience required (current roster not required)</li>
              <li>▸ Fully padded — arrive with required football equipment</li>
              <li>▸ No invitation needed to register and attend</li>
            </ul>
          </Card>
        </div>
      </section>

      <section className="bg-navy py-16 sm:py-24">
        <div className="wrap grid gap-12 lg:grid-cols-[0.8fr_1.2fr] items-start">
          <SectionHeading eyebrow="Tournament Fees" title={<><DollarSign className="inline h-10 w-10 -mt-2 text-gold" />Uniform Packages</>} sub="Choose the package that's right for your athlete." />
          <Packages />
        </div>
      </section>

      <section className="bg-pitch py-16 sm:py-24">
        <div className="wrap grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          <Card icon={CalendarClock} title="Practice Schedules" testid="parents-practice">
            <p>Practices begin following the conclusion of the selected team's regular season.</p>
            <p>Location: PSLAA Field, {SITE.address}. Team-specific schedules are posted on each team page and in the parent portal.</p>
          </Card>
          <Card icon={Shirt} title="Tournament Dates" testid="parents-tournament-dates">
            <ul className="space-y-2">
              {(events || []).map((e) => <li key={e.id}><Link to={`/events/${e.slug || e.id}`} className="hover:text-gold"><strong className="text-white">{fmtDate(e.date)}</strong> — {e.title}</Link></li>)}
            </ul>
            <p className="text-xs text-slate-500">The postseason includes two tournament weekends.</p>
          </Card>
          <Card icon={Plane} title="Travel Information" testid="parents-travel">
            <p>Travel details, hotel information and itineraries for tournament weekends will be shared with selected families once confirmed.</p>
            <p>Approved families will receive updates in the parent portal.</p>
          </Card>
        </div>
      </section>

      <section className="bg-navy py-16 sm:py-24">
        <div className="wrap grid gap-12 lg:grid-cols-2">
          <div data-testid="parents-announcements">
            <p className="eyebrow mb-5 flex items-center gap-2"><Megaphone className="h-4 w-4" /> Important Announcements</p>
            <div className="space-y-px bg-line/60">
              {(ann || []).map((a) => <div key={a.id} className="bg-navy py-4"><p className="font-mono text-[10px] uppercase tracking-widest text-slate-400">{fmtDate(a.date)}</p><p className="font-display text-xl font-extrabold uppercase italic">{a.title}</p><p className="text-sm text-slate-300">{a.body}</p></div>)}
            </div>
          </div>
          <div className="card-navy p-6 sm:p-8 self-start" data-testid="parents-contact">
            <p className="eyebrow mb-3">Contact</p>
            <p className="font-display text-3xl font-black uppercase italic">Coach KP</p>
            <p className="text-sm text-slate-400">President / Program Director</p>
            <div className="mt-5 flex flex-col gap-3">
              <a href={SITE.tel} className="btn-gold" data-testid="parents-call-button"><Phone className="h-4 w-4" /> {SITE.phone}</a>
              <a href={SITE.mailto} className="btn-line break-all !text-sm" data-testid="parents-email-button"><Mail className="h-4 w-4" /> {SITE.email}</a>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-pitch py-16 sm:py-24">
        <div className="wrap grid gap-12 lg:grid-cols-[0.8fr_1.2fr]">
          <SectionHeading eyebrow="FAQ" title="Frequently Asked Questions" />
          <FaqList prefix="parents-faq" />
        </div>
      </section>
    </div>
  );
}
